import React from "react";
import { FieldValues, SubmitHandler } from "react-hook-form";
import { DocumentData, WithFieldValue } from "firebase/firestore";

import { AuthUser, MODULE_LOCATIONS_LIST } from "@constants";
import { ButtonModal, JsonForm, JsonFormControl } from "@components";

interface IEditUserModal {
  isViewerAdmin: boolean;
  user: AuthUser;
  updateUser: (data: WithFieldValue<DocumentData>) => Promise<void>;
}

function makeNonAdminFormControl(userDetails: AuthUser): JsonFormControl[] {
  return [
    {
      label: "First Name",
      inputType: "text",
      fieldName: "firstName",
      defaultValue: userDetails.firstName,
      autocomplete: "firstName",
      placeholder: "First Name",
      config: {
        required: "First Name is required",
      },
    },
    {
      label: "Last Name",
      inputType: "text",
      fieldName: "lastName",
      defaultValue: userDetails.lastName,
      autocomplete: "lastName",
      placeholder: "Last Name",
      config: {
        required: "Last Name is required",
      },
    },
    {
      label: "Location",
      inputType: "select",
      fieldName: "location",
      defaultValue: userDetails.location,
      autocomplete: "location",
      options: MODULE_LOCATIONS_LIST.map((loc) => ({ label: loc, value: loc })),
      config: {
        required: "Location is required",
      },
    },
  ];
}

function makeAdminFormControl(
  userDetails: AuthUser,
  isAdmin: boolean,
): JsonFormControl[] {
  if (!isAdmin) return [];
  return [
    {
      label: "role",
      inputType: "select",
      fieldName: "role",
      defaultValue: userDetails.role,
      options: [
        { label: "user", value: "user" },
        { label: "admin", value: "admin" },
      ],
      config: {
        required: 'Role must be either "user" or "admin"',
      },
    },
  ];
}

function makeFormControl(
  userDetails: AuthUser,
  isAdmin: boolean,
): JsonFormControl[] {
  return [
    ...makeNonAdminFormControl(userDetails),
    ...makeAdminFormControl(userDetails, isAdmin),
  ];
}

function EditUserModal({ user, updateUser, isViewerAdmin }: IEditUserModal) {
  const formControl = React.useMemo<JsonFormControl[]>(
    () => makeFormControl(user, isViewerAdmin),
    [user, isViewerAdmin],
  );

  return (
    <ButtonModal
      label="Edit User Details"
      title={
        user
          ? `Editing Details for ${user.firstName} ${user.lastName}`
          : "Editing User Details"
      }
    >
      <JsonForm
        fields={formControl}
        onSubmit={updateUser as SubmitHandler<FieldValues>}
      />
    </ButtonModal>
  );
}

export { EditUserModal };
